import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Save, Cancel, ArrowBack, LockReset } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import principalAPI from '../../services/principalAPI';
import { useAuth } from '../../context/AuthContext';

const AddSchoolAdmin = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  // Fetch admin data when in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchAdmin = async () => {
        try {
          setLoading(true);
          // Get the list and find the specific admin
          const response = await principalAPI.getSchoolAdmins();
          const admin = (response.data.schoolAdmins || []).find(a => a.id === id);
          if (admin) {
            setFormData({
              name: admin.name || '',
              email: admin.email || '',
              phone: admin.phone || ''
            });
          } else {
            setError('School admin not found');
          }
        } catch (error) {
          console.error('Error fetching school admin:', error);
          setError('Failed to load school admin data');
        } finally {
          setLoading(false);
        }
      };
      fetchAdmin();
    }
  }, [isEditMode, id]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isEditMode) {
        const updateData = { name: formData.name, email: formData.email, phone: formData.phone };
        await principalAPI.updateSchoolAdmin(id, updateData);
        setSuccess('School Admin updated successfully!');
      } else {
        await principalAPI.createSchoolAdmin(formData);
        setSuccess('School Admin created successfully! A password setup link has been emailed to them.');
        setFormData({ name: '', email: '', phone: '' });
      }

      setTimeout(() => {
        navigate('/principal/school-admins');
      }, 2000);

    } catch (error) {
      setError(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} school admin`);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setResetLoading(true);
    setError('');
    try {
      await principalAPI.resetSchoolAdminPassword(id);
      setSuccess('Password reset link sent to the admin\'s email!');
      setResetDialogOpen(false);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send reset link');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          {isEditMode ? 'Edit School Admin' : 'Add New School Admin'}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/principal/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Personal Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter school admin's full name"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+1234567890"
              />
            </Grid>

            {/* Set Login */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                Set Login
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Set Email ID"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="admin@example.com"
                helperText={isEditMode ? "Changing email will update email ID" : "This will be used as email ID"}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {isEditMode ? (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<LockReset />}
                    onClick={() => setResetDialogOpen(true)}
                    sx={{ py: 1.5 }}
                  >
                    Reset Password
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    A password setup link will be emailed to the admin.
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={() => navigate('/principal/school-admins')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  disabled={loading}
                >
                  {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update School Admin' : 'Create School Admin')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset School Admin Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            A password reset link will be emailed to <strong>{formData.name}</strong> ({formData.email}). The link expires in 1 hour.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            color="warning"
            disabled={resetLoading}
            startIcon={resetLoading ? <CircularProgress size={20} /> : <LockReset />}
          >
            {resetLoading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AddSchoolAdmin;
